import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, resolveAsset } from '../api/client';

// Public, standalone (no site chrome) viewer for a whole story, rendered as page
// images. Two audiences share it: the shareable watermarked "demo"
// of a story — the link an admin copies from the previews table and sends a
// customer. Fetches GET /api/stories/:shareToken/demo, which renders every book
// page to a JPEG with a big www.anabatal.com watermark burned in (cached on the
// story), then displays them. Keyed by the unguessable shareToken, like /preview.
// The first (uncached) load runs a headless-Chrome render, so it can take a few
// seconds — hence the loader. Uses the site's global design tokens (App.css).

// Western → Arabic-Indic digits for the page-number badges.
const AR = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
const arNum = (n) => String(n).replace(/\d/g, (d) => AR[+d]);

export default function Demo() {
  const { shareToken } = useParams();
  // 'loading' | 'pending' (still generating) | 'ready' | 'error'
  const [status, setStatus] = useState('loading');
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    setStatus('loading');
    api.get(`/api/stories/${shareToken}/demo`)
      .then((d) => {
        if (!alive) return;
        setData(d);
        setStatus(d.ready ? 'ready' : 'pending');
      })
      .catch(() => { if (alive) setStatus('error'); });
    return () => { alive = false; };
  }, [shareToken]);

  const images = (data && data.images) || [];

  return (
    <div className="demo-root" dir="rtl">
      <style>{CSS}</style>

      <div className="demo-wrap">
        <header className="demo-hero">
          <div className="demo-brand">أنا البطل</div>
          {data?.title && <h1 className="demo-title">{data.title}</h1>}
          {status === 'ready' && (
            <>
              <div className="demo-sub">معاينة الكتاب — {arNum(images.length)} صفحة</div>
              <div className="demo-badge">🔒 نسخة تجريبية — بعلامة مائية عبر كل صفحة</div>
            </>
          )}
        </header>

        {status === 'loading' && (
          <div className="demo-notice">
            <div className="demo-spinner" />
            <p>جارٍ تجهيز النسخة التجريبية…</p>
          </div>
        )}

        {status === 'pending' && (
          <div className="demo-notice">
            <div className="demo-notice-emoji">⏳</div>
            <h2>القصة قيد التحضير</h2>
            <p>ما زلنا نجهّز هذه القصة. حاول مرة أخرى بعد قليل.</p>
          </div>
        )}

        {status === 'error' && (
          <div className="demo-notice">
            <div className="demo-notice-emoji">🌙</div>
            <h2>تعذّر تجهيز النسخة التجريبية</h2>
            <p>حدث خطأ أثناء تجهيز الصفحات. حاول مرة أخرى بعد قليل.</p>
          </div>
        )}

        {status === 'ready' && (
          <>
            <div className="demo-pages">
              {images.map((u, i) => (
                <figure className={`demo-card${i === 0 ? ' demo-card--cover' : ''}`} key={i}>
                  {i > 0 && <span className="demo-num">{arNum(i)}</span>}
                  <img
                    src={resolveAsset(u)}
                    alt={i === 0 ? 'الغلاف' : `صفحة ${i}`}
                    loading={i < 2 ? 'eager' : 'lazy'}
                    onContextMenu={(e) => e.preventDefault()}
                    draggable={false}
                  />
                </figure>
              ))}
            </div>

            <div className="demo-foot">© أنا البطل — www.anabatal.com</div>
          </>
        )}
      </div>

      {status === 'ready' && (
        <div className="demo-cta-bar">
          {data?.showcase ? (
            // Homepage sample book: the reader has just finished one of OUR
            // stories, so the next step is creating the same kind of story for
            // their own child — not buying this particular copy.
            <Link
              className="demo-cta-btn"
              to={data.templateSlug ? `/create/${data.templateSlug}` : '/'}
            >
              أريد قصة مثل هذه لطفلي ✨
            </Link>
          ) : (
            <Link className="demo-cta-btn" to={`/preview/${shareToken}`}>احصل على نسختك الكاملة ✨</Link>
          )}
        </div>
      )}
    </div>
  );
}

const CSS = `
  .demo-root {
    font-family: 'Cairo', system-ui, sans-serif;
    background: var(--gradient-sky, linear-gradient(160deg, #ede9fe 0%, #fce7f3 50%, #fef3c7 100%));
    color: var(--ink, #1e1b3a);
    min-height: 100vh;
  }
  .demo-wrap { max-width: 600px; margin: 0 auto; padding: 26px 16px 120px; }

  /* ── Hero ── */
  .demo-hero { text-align: center; padding: 8px 0 22px; }
  .demo-brand {
    display: inline-block; font-weight: 800; font-size: .95rem; letter-spacing: .3px;
    color: var(--accent, #7c3aed);
    background: rgba(124, 58, 237, 0.10);
    padding: 5px 16px; border-radius: 999px;
  }
  .demo-title {
    font-family: 'Expo Arabic', 'Cairo', system-ui, sans-serif;
    font-size: clamp(1.5rem, 6vw, 2.1rem); font-weight: 800; line-height: 1.25;
    margin: 14px 0 8px;
    background: var(--gradient-dream, linear-gradient(135deg, #7c3aed 0%, #ec4899 50%, #f59e0b 100%));
    -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
  }
  .demo-sub { color: var(--ink-soft, #564f7a); font-size: .95rem; font-weight: 600; }
  .demo-badge {
    display: inline-block; margin-top: 12px; font-size: .82rem; font-weight: 700;
    color: #92400e; background: var(--gold-soft, #fde68a);
    padding: 6px 14px; border-radius: 999px;
  }

  /* ── Pages: every page is a uniform full-width card whose image fills it edge
        to edge — so there is never empty space beside a page, whatever its aspect
        ratio (portrait cover, landscape spread, square). ── */
  .demo-pages { display: flex; flex-direction: column; gap: 16px; }
  .demo-card {
    position: relative; margin: 0; overflow: hidden; line-height: 0;
    background: var(--bg-card, #fff);
    border: 1px solid var(--border, #e9e3f7);
    border-radius: var(--radius-sm, 14px);
    box-shadow: var(--shadow-sm, 0 6px 18px -4px rgba(124, 58, 237, 0.14));
  }
  .demo-card img { display: block; width: 100%; height: auto; user-select: none; -webkit-user-drag: none; }
  /* The cover leads with a stronger, book-like shadow. */
  .demo-card--cover {
    margin-bottom: 6px;
    box-shadow: 0 20px 48px -14px rgba(30, 27, 58, 0.38);
  }
  .demo-num {
    position: absolute; top: 10px; inset-inline-start: 10px; z-index: 2;
    min-width: 26px; height: 26px; padding: 0 8px;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: .8rem; font-weight: 800; color: #fff;
    background: rgba(30, 27, 58, 0.55); border-radius: 999px; backdrop-filter: blur(2px);
  }

  .demo-foot { text-align: center; color: var(--ink-hint, #8e87b3); font-size: .82rem; margin-top: 28px; }

  /* ── Notices ── */
  .demo-notice { text-align: center; padding: 70px 24px; color: var(--ink-soft, #564f7a); }
  .demo-notice-emoji { font-size: 2.4rem; margin-bottom: 10px; }
  .demo-notice h2 { font-size: 1.25rem; margin: 0 0 8px; color: var(--ink, #1e1b3a); }
  .demo-notice p { line-height: 1.7; margin: 0; }
  .demo-spinner {
    width: 40px; height: 40px; margin: 0 auto 18px; border-radius: 50%;
    border: 4px solid var(--accent-soft, #c4b5fd); border-top-color: var(--accent, #7c3aed);
    animation: demo-spin .9s linear infinite;
  }
  @keyframes demo-spin { to { transform: rotate(360deg); } }

  /* ── Floating buy button ── */
  .demo-cta-bar {
    position: fixed; inset-inline: 0; bottom: 0; z-index: 10;
    display: flex; justify-content: center;
    padding: 14px 16px calc(14px + env(safe-area-inset-bottom, 0px));
    background: linear-gradient(to top, rgba(250, 247, 255, 0.96) 55%, rgba(250, 247, 255, 0));
    pointer-events: none;
  }
  .demo-cta-btn {
    pointer-events: auto;
    display: inline-block; width: 100%; max-width: 420px; text-align: center;
    background: var(--gradient-dream, linear-gradient(135deg, #7c3aed 0%, #ec4899 60%, #f59e0b 120%));
    color: #fff; text-decoration: none; font-weight: 800; font-size: 1.05rem;
    padding: 15px 28px; border-radius: 999px;
    box-shadow: 0 12px 30px -6px rgba(124, 58, 237, 0.5);
  }
  .demo-cta-btn:active { transform: translateY(1px); }
`;
